/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {FilterModel} from 'ag-grid-community';
import {errorOptions} from '@ion/applications/src/prompts/constants';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {replayOfflineCanRequest, updateOfflineCanRequest} from '@ion/core/src/services/Connect';
import {Vehicle} from '@ion/core/src/types/entities';
import {OfflineCanApiResponse, OfflineCanRequestResult} from '@ion/core/src/types/services';
import {formatErrorMessage} from '@ion/dls/src/utils/ComponentUtils';
import {QueryChipData} from '@ion/features/src/search/types';
import {
  CanRequestBulkRetryResult,
  handleCanRequestBulkRetry,
  handleCanRequestCancel,
  handleCanRequestRetry,
  resolveCanRequestErrorDescription
} from '../manager/CanRequestUtils';
import {SearchFields} from '../utils';

/**
 * Supported CAN requests page tabs.
 */
export enum RequestTab {
  My = 'my',
  All = 'all'
}

const MY_REQUESTS_PREFERENCE  = 'vehicle-canrequests-my';
const ALL_REQUESTS_PREFERENCE = 'vehicle-canrequests-all';
const ALL_REQUESTS_START_DATE = '1970-01-01T00:00:00Z';

interface RetryCanRequestParams {
  request: OfflineCanRequestResult;
  dispatch: (action: unknown) => void;
  setRetryingRequestId: (requestId: string | null) => void;
  selectedCanEsn?: string;
  onSuccess?: () => void;
}

interface CancelCanRequestParams {
  request: OfflineCanRequestResult;
  dispatch: (action: unknown) => void;
  setCancellingRequestId: (requestId: string | null) => void;
  onSuccess?: () => void;
}

interface CanRequestFeedbackParams {
  dispatch: (action: unknown) => void;
  successMessage: string;
  errorMessage: string;
  onSuccess?: () => void;
}

interface BulkRetryCanRequestParams {
  requests: OfflineCanRequestResult[];
  dispatch: (action: unknown) => void;
  setBulkRetrying: (value: boolean) => void;
  selectedCanEsn?: string;
  onClearSelection: () => void;
  onSuccess?: () => void;
}

/**
 * Resolves the saved grid-layout preference key for the selected CAN requests tab.
 *
 * ```typescript
 * import {getPreference} from
 *   '@ion/applications/src/pages/vehicle/canrequests/CanRequestUtils';
 *
 * const preference = getPreference(RequestTab.My);
 * // 'vehicle-canrequests-my'
 * ```
 *
 * @param   tab The active CAN requests tab.
 * @returns The user preference key for that tab.
 */
export const getPreference = (tab: RequestTab): string => (
  tab === RequestTab.My ? MY_REQUESTS_PREFERENCE : ALL_REQUESTS_PREFERENCE
);

const getFilterPreferencePath = (tab: RequestTab): string => (
  `${getPreference(tab)}:grid.filters`
);

/**
 * Reads the persisted AG Grid filter model for the selected CAN requests tab.
 *
 * ```typescript
 * import {readFilterModel} from
 *   '@ion/applications/src/pages/vehicle/canrequests/CanRequestUtils';
 *
 * const filterModel = readFilterModel(RequestTab.All);
 * ```
 *
 * @param   tab The active CAN requests tab.
 * @returns The saved AG Grid filter model, or an empty model when preferences are unavailable.
 */
export const readFilterModel = (tab: RequestTab): FilterModel => {
  if (!UserPreferences.isInitialized()) {
    return {};
  }
  return UserPreferences.get(getFilterPreferencePath(tab)) || {};
};

/**
 * Persists an AG Grid filter model for the selected CAN requests tab.
 *
 * ```typescript
 * import {saveFilterModel} from
 *   '@ion/applications/src/pages/vehicle/canrequests/CanRequestUtils';
 *
 * saveFilterModel(RequestTab.My, filterModel);
 * ```
 *
 * @param tab         The active CAN requests tab.
 * @param filterModel The AG Grid filter model to persist.
 */
export const saveFilterModel = (tab: RequestTab, filterModel: FilterModel): void => {
  if (UserPreferences.isInitialized()) {
    UserPreferences.set(getFilterPreferencePath(tab), filterModel || {}, !UserPreferences.hasChangeDetection());
  }
};

const getSearchValue = (queries: QueryChipData[] = [], attribute: string): string => (
  `${queries.find((query) => query?.attribute === attribute)?.value || ''}`.trim()
);

/**
 * Builds offline CAN request query params from the active tab, current user, and search chips.
 *
 * ```typescript
 * import {createQueryParams} from
 *   '@ion/applications/src/pages/vehicle/canrequests/CanRequestUtils';
 *
 * const params = createQueryParams(RequestTab.My, 'jsmith', queries);
 * ```
 *
 * @param   activeTab The selected request tab.
 * @param   username  The authenticated user's username.
 * @param   queries   Modular search chips selected for the active tab.
 * @returns Query params for `useLoadOfflineCanRequests`.
 */
export const createQueryParams = (
  activeTab: RequestTab,
  username: string,
  queries: QueryChipData[]
) => {
  const esn       = getSearchValue(queries, SearchFields.ESN);
  const status    = getSearchValue(queries, SearchFields.STATUS);
  const createdBy = getSearchValue(queries, SearchFields.USERNAME);
  const params    = {
    ...(esn ? {esn} : {}),
    ...(status ? {status: status.toUpperCase()} : {}),
    ...(activeTab === RequestTab.My && username ? {createdBy: username} : {}),
    ...(activeTab === RequestTab.All && createdBy ? {createdBy} : {})
  };

  return Object.keys(params).length ? params : {'createdOn[gte]': ALL_REQUESTS_START_DATE};
};

/**
 * Converts the offline CAN requests API response into the paginated grid payload.
 *
 * ```typescript
 * const page = parseOfflineCanRequestsPage(response);
 * ```
 *
 * @param   response Offline CAN request API response.
 * @returns Paginated grid rows and total count.
 */
export const parseOfflineCanRequestsPage = (
  response: OfflineCanApiResponse<OfflineCanRequestResult>
) => {
  const items = response?.response?.results || [];
  return {
    items,
    totalCount : response?.response?.totalCount ?? response?.totalCount ?? items.length
  };
};

const getVehicleEsns = (vehicle?: Vehicle | null): string[] => {
  const vehicleEsn = vehicle?.esn;
  const esns = Array.isArray(vehicleEsn) ? vehicleEsn : [`${vehicleEsn || ''}`];
  return esns.map((esn) => `${esn || ''}`.trim()).filter(Boolean);
};

/**
 * Selects the best ESN from a vehicle, preferring one that matches the user's search text.
 *
 * ```typescript
 * import {getVehicleEsn} from
 *   '@ion/applications/src/pages/vehicle/canrequests/CanRequestUtils';
 *
 * const esn = getVehicleEsn(vehicle, '123');
 * ```
 *
 * @param   vehicle      Vehicle profile selected from create-request search results.
 * @param   preferredEsn Optional ESN search text used to prefer one of multiple vehicle ESNs.
 * @returns The matching ESN, the first available ESN, or an empty string.
 */
export const getVehicleEsn = (vehicle?: Vehicle | null, preferredEsn = ''): string => {
  const esns                   = getVehicleEsns(vehicle);
  const normalizedPreferredEsn = preferredEsn.trim().toLowerCase();
  return esns.find((esn) => esn.toLowerCase().includes(normalizedPreferredEsn)) || esns[0] || '';
};

/**
 * Formats a vehicle option label for the create CAN request device selector.
 *
 * ```typescript
 * import {getVehicleOptionLabel} from
 *   '@ion/applications/src/pages/vehicle/canrequests/CanRequestUtils';
 *
 * const label = getVehicleOptionLabel(vehicle);
 * ```
 *
 * @param   vehicle Vehicle profile returned by vehicle manager search.
 * @returns A display label containing VIN and/or ESN values.
 */
export const getVehicleOptionLabel = (vehicle?: Vehicle | null): string => {
  const esn = getVehicleEsns(vehicle).join(', ');
  const vin = `${vehicle?.vin || ''}`.trim();

  return vin && esn ? `${vin} (${esn})` : esn || vin;
};

/*
 * Retry and cancel share one feedback shape: a transient success prompt that
 * chains the caller's refresh, and an error prompt that prefers the API's own
 * description over the generic fallback message.
 */
const createCanRequestFeedback = ({
  dispatch,
  successMessage,
  errorMessage,
  onSuccess
}: CanRequestFeedbackParams) => ({
  onSuccess : function() {
    dispatch(toggleUserPrompt({
      message          : successMessage,
      actionLabel      : translate('common:dismiss'),
      autoHide         : true,
      autoHideDuration : 3000,
      type             : 'success',
      translateText    : false
    }));
    onSuccess?.();
  },
  onError : function(err: any) {
    error(err);
    dispatch(toggleUserPrompt({
      ...errorOptions,
      message       : resolveCanRequestErrorDescription(err) || formatErrorMessage(err) || errorMessage,
      actionLabel   : translate('common:tryAgain'),
      errorState    : true,
      translateText : false
    }));
  }
});

/**
 * Retries an offline CAN request and dispatches success/error feedback.
 *
 * ```typescript
 * await retryCanRequest({
 *   request,
 *   dispatch,
 *   setRetryingRequestId,
 *   onSuccess: refresh
 * });
 * ```
 *
 * @param request              The CAN request selected for retry.
 * @param dispatch             Redux dispatch used for user feedback prompts.
 * @param setRetryingRequestId Setter used to track the active retry row.
 * @param selectedCanEsn       ESN used when the request carries no ESN of its own.
 * @param onSuccess            Optional callback invoked after a successful retry.
 */
export const retryCanRequest = async({
  request,
  dispatch,
  setRetryingRequestId,
  selectedCanEsn = '',
  onSuccess
}: RetryCanRequestParams): Promise<void> => {
  await handleCanRequestRetry({
    request,
    selectedCanEsn,
    setRetryingRequestId,
    replayOfflineCanRequestFn : replayOfflineCanRequest,
    ...createCanRequestFeedback({
      dispatch,
      successMessage : translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.retrySuccess'),
      errorMessage   : translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.retryError'),
      onSuccess
    })
  });
};

/**
 * Cancels a pending offline CAN request and dispatches success/error feedback.
 *
 * ```typescript
 * await cancelCanRequest({
 *   request,
 *   dispatch,
 *   setCancellingRequestId,
 *   onSuccess: refresh
 * });
 * ```
 *
 * @param request                The CAN request selected for cancel.
 * @param dispatch               Redux dispatch used for user feedback prompts.
 * @param setCancellingRequestId Setter used to track the active cancel row.
 * @param onSuccess              Optional callback invoked after a successful cancel.
 */
export const cancelCanRequest = async({
  request,
  dispatch,
  setCancellingRequestId,
  onSuccess
}: CancelCanRequestParams): Promise<void> => {
  await handleCanRequestCancel({
    request,
    setCancellingRequestId,
    updateOfflineCanRequestFn : updateOfflineCanRequest,
    ...createCanRequestFeedback({
      dispatch,
      successMessage : translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.cancelSuccess'),
      errorMessage   : translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.cancelError'),
      onSuccess
    })
  });
};

/**
 * Retries a batch of offline CAN requests grouped by ESN, dispatching one
 * consolidated success / partial-failure / failure feedback toast. Any
 * submission (full or partial) clears the entire selection via
 * `onClearSelection`, since the still-failing ids are no longer guaranteed
 * eligible once the grid refreshes; only a total failure leaves the
 * selection untouched so the user can retry as-is.
 *
 * ```typescript
 * await bulkRetryCanRequests({
 *   requests: selectedRequests,
 *   dispatch,
 *   setBulkRetrying,
 *   onClearSelection,
 *   onSuccess: refresh
 * });
 * ```
 *
 * @param requests         The selected CAN request entries to retry.
 * @param dispatch         Redux dispatch used for user feedback prompts.
 * @param setBulkRetrying  Setter toggling the bulk-retry loading state.
 * @param selectedCanEsn   Fallback ESN for entries missing one.
 * @param onClearSelection Invoked to clear the selection once at least one
 *                         request submitted successfully.
 * @param onSuccess        Optional callback invoked once the batch settles.
 */
export const bulkRetryCanRequests = async({
  requests,
  dispatch,
  setBulkRetrying,
  selectedCanEsn = '',
  onClearSelection,
  onSuccess
}: BulkRetryCanRequestParams): Promise<void> => {
  await handleCanRequestBulkRetry({
    requests,
    selectedCanEsn,
    setBulkRetrying,
    replayOfflineCanRequestFn : replayOfflineCanRequest,
    onSettled                 : function(result: CanRequestBulkRetryResult) {
      const failedCount = result.failures.reduce((total, failure) => total + failure.requestIds.length, 0);
      if (failedCount === 0) {
        createCanRequestFeedback({
          dispatch,
          successMessage : translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.bulk.retrySuccess', {
            count : result.submittedIds.length
          }),
          errorMessage : '',
          onSuccess    : onClearSelection
        }).onSuccess();
      } else if (result.submittedIds.length > 0) {
        dispatch(toggleUserPrompt({
          ...errorOptions,
          message : translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.bulk.retryPartialError', {
            successCount : result.submittedIds.length,
            totalCount   : result.submittedIds.length + failedCount,
            failureCount : failedCount
          }),
          actionLabel   : translate('common:tryAgain'),
          errorState    : true,
          translateText : false
        }));
        onClearSelection();
      } else {
        /*
         * A total failure can span multiple ESN groups, each with its own
         * error, so no single one is representative — the toast uses the
         * generic message instead of an arbitrary failure's description.
         */
        result.failures.forEach((failure) => error(failure.error));
        dispatch(toggleUserPrompt({
          ...errorOptions,
          message       : translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.bulk.retryError'),
          actionLabel   : translate('common:tryAgain'),
          errorState    : true,
          translateText : false
        }));
      }
      onSuccess?.();
    }
  });
};
